// الگوهای صفحه (AP-008): PageHeader، ListPage، DetailPage، FormPage، FormDialog.
// هر صفحه شناسه SCR-... را در کامنت فایل و data-screen-id روی ریشه دارد (Playwright).
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@phoenix/i18n';
import { createContext, useContext, useEffect, useId, type FormEvent, type ReactNode } from 'react';
import { useBlocker } from 'react-router';
import { ConfirmDialog } from '../components/dialogs';
import { ErrorState, LoadingState } from '../components/states';

/**
 * صفحه درون صفحه دیگر (مثلاً پنل مرحله Setup Wizard — AP-PLT-04): عنوان PageHeader به‌جای h1 با سطح h3 و اندازه کوچک‌تر نمایش داده
 * می‌شود تا ساختار سرفصل‌ها (دسترس‌پذیری) درست بماند و صفحه تنظیمات بدون کپی دوباره استفاده شود.
 */
const EmbeddedPageContext = createContext(false);

export function EmbeddedPage({ children }: { children: ReactNode }) {
  return <EmbeddedPageContext.Provider value>{children}</EmbeddedPageContext.Provider>;
}

/** صفحه جاری درون صفحه دیگر نمایش داده می‌شود. */
export function useIsEmbeddedPage(): boolean {
  return useContext(EmbeddedPageContext);
}

export interface PageHeaderProps {
  titleKey: string;
  titleValues?: Record<string, unknown>;
  subtitleKey?: string;
  /** نشان‌ها کنار عنوان (StatusChip، SampleBadge). */
  badges?: ReactNode;
  actions?: ReactNode;
}

export function PageHeader({ titleKey, titleValues, subtitleKey, badges, actions }: PageHeaderProps) {
  const { t } = useTranslation();
  const embedded = useIsEmbeddedPage();
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {embedded ? (
            <Typography variant="h3" component="h3">
              {t(titleKey, titleValues)}
            </Typography>
          ) : (
            <Typography variant="h1">{t(titleKey, titleValues)}</Typography>
          )}
          {badges}
        </Stack>
        {subtitleKey && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6, maxWidth: 720 }}>
            {t(subtitleKey)}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

export interface PageFrameProps {
  /** شناسه صفحه (SCR-...) برای data-screen-id. */
  screenId: string;
  children: ReactNode;
}

function PageFrame({ screenId, children }: PageFrameProps) {
  return (
    <Box data-screen-id={screenId} component="section">
      {children}
    </Box>
  );
}

export interface ListPageProps extends PageHeaderProps {
  screenId: string;
  /** فیلترها (بالای جدول). */
  filters?: ReactNode;
  /** جدول (DataTable که خودش حالت‌های Loading/Empty/Error را دارد). */
  children: ReactNode;
}

/** فهرست: سربرگ با اقدام‌ها، فیلتر و جدول. */
export function ListPage({ screenId, filters, children, ...header }: ListPageProps) {
  return (
    <PageFrame screenId={screenId}>
      <PageHeader {...header} />
      {filters && <Box sx={{ mb: 1.5 }}>{filters}</Box>}
      {children}
    </PageFrame>
  );
}

export interface DetailTab {
  id: string;
  labelKey: string;
  content: ReactNode;
}

export interface DetailPageProps extends PageHeaderProps {
  screenId: string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  tabs: readonly DetailTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** بنر مشترک بین سربرگ و تب‌ها (مثل COMP-PATIENT-HEADER). */
  banner?: ReactNode;
}

/** جزئیات با تب‌ها؛ Loading و Error در سطح صفحه. */
export function DetailPage({
  screenId,
  isLoading,
  error,
  onRetry,
  tabs,
  activeTab,
  onTabChange,
  banner,
  ...header
}: DetailPageProps) {
  const { t } = useTranslation();
  const baseId = useId();
  if (isLoading) {
    return (
      <PageFrame screenId={screenId}>
        <LoadingState rows={4} />
      </PageFrame>
    );
  }
  if (error) {
    return (
      <PageFrame screenId={screenId}>
        <ErrorState error={error} onRetry={onRetry} />
      </PageFrame>
    );
  }
  const current = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
  return (
    <PageFrame screenId={screenId}>
      <PageHeader {...header} />
      {banner && <Box sx={{ mb: 2 }}>{banner}</Box>}
      <Tabs value={current?.id ?? false} onChange={(_, value: string) => onTabChange(value)} sx={{ mb: 2 }}>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={t(tab.labelKey)}
            id={`${baseId}-tab-${tab.id}`}
            aria-controls={`${baseId}-panel-${tab.id}`}
          />
        ))}
      </Tabs>
      {current && (
        <Box
          role="tabpanel"
          id={`${baseId}-panel-${current.id}`}
          aria-labelledby={`${baseId}-tab-${current.id}`}
        >
          {current.content}
        </Box>
      )}
    </PageFrame>
  );
}

/** Ctrl+S (و Cmd+S) ← ذخیره فرم (IA §5). */
export function useSaveShortcut(onSave: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        onSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onSave, enabled]);
}

/**
 * محافظ تغییرات ذخیره‌نشده: ناوبری درون‌برنامه‌ای با دیالوگ تأیید و بستن/تازه‌سازی صفحه با هشدار مرورگر.
 * نیازمند Data Router (createBrowserRouter).
 */
export function DirtyGuard({ when }: { when: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => when && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!when) {
      return undefined;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      titleKey="common:form.dirty_title"
      messageKey="common:form.dirty_message"
      confirmKey="common:form.leave"
      destructive
      onConfirm={() => blocker.proceed?.()}
      onCancel={() => blocker.reset?.()}
    />
  );
}

export interface FormPageProps extends PageHeaderProps {
  screenId: string;
  onSubmit: (event?: FormEvent) => void;
  isDirty: boolean;
  isSubmitting: boolean;
  /** خطای ثبت (ApiError) — زیر فرم با ErrorState. */
  submitError?: unknown;
  isLoading?: boolean;
  loadError?: unknown;
  onCancel?: () => void;
  submitKey?: string;
  /** فقط‌خواندنی (بدون Permission یا حالت دسترسی غیر Full): دکمه ثبت غیرفعال و Ctrl+S بی‌اثر. */
  readOnly?: boolean;
  /** پیام بالای فرم (مثلاً موفقیت ذخیره یا دلیل فقط‌خواندنی). */
  notice?: ReactNode;
  children: ReactNode;
}

/** فرم تمام‌صفحه: RHF + Zod، Dirty guard، Ctrl+S، چهار حالت. */
export function FormPage({
  screenId,
  onSubmit,
  isDirty,
  isSubmitting,
  submitError,
  isLoading,
  loadError,
  onCancel,
  submitKey,
  readOnly = false,
  notice,
  children,
  ...header
}: FormPageProps) {
  const { t } = useTranslation('common');
  useSaveShortcut(() => onSubmit(), !isSubmitting && !isLoading && !readOnly);

  if (isLoading) {
    return (
      <PageFrame screenId={screenId}>
        <LoadingState rows={4} />
      </PageFrame>
    );
  }
  if (loadError) {
    return (
      <PageFrame screenId={screenId}>
        <ErrorState error={loadError} />
      </PageFrame>
    );
  }

  return (
    <PageFrame screenId={screenId}>
      <DirtyGuard when={isDirty && !isSubmitting} />
      <PageHeader {...header} />
      {notice && <Box sx={{ mb: 2 }}>{notice}</Box>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          component="form"
          noValidate
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!readOnly) {
              onSubmit(event);
            }
          }}
        >
          <Stack spacing={2}>{children}</Stack>
          {submitError ? <ErrorState error={submitError} compact /> : null}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || readOnly}
              data-testid="form-submit"
            >
              {t(submitKey ?? 'common:actions.save')}
            </Button>
            {onCancel && (
              <Button onClick={onCancel} disabled={isSubmitting}>
                {t('common:actions.cancel')}
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
    </PageFrame>
  );
}

export interface FormDialogProps {
  open: boolean;
  titleKey: string;
  screenId?: string;
  onSubmit: (event?: FormEvent) => void;
  onClose: () => void;
  isDirty: boolean;
  isSubmitting: boolean;
  submitError?: unknown;
  submitKey?: string;
  /** عرض دیالوگ (پیش‌فرض sm). */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  children: ReactNode;
}

/** فرم در دیالوگ: Esc بستن (با تأیید اگر Dirty)، Enter/Ctrl+S ثبت. */
export function FormDialog({
  open,
  titleKey,
  screenId,
  onSubmit,
  onClose,
  isDirty,
  isSubmitting,
  submitError,
  submitKey,
  maxWidth = 'sm',
  children,
}: FormDialogProps) {
  const { t } = useTranslation('common');
  const titleId = useId();
  useSaveShortcut(() => onSubmit(), open && !isSubmitting);

  const requestClose = () => {
    if (isSubmitting) {
      return;
    }
    // محافظ ساده دیالوگ: ConfirmDialog تودرتو در دیالوگ مدیریت تمرکز را پیچیده می‌کند.
    if (!isDirty || window.confirm(t('common:form.dirty_message'))) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      aria-labelledby={titleId}
      maxWidth={maxWidth}
      fullWidth
      data-screen-id={screenId}
    >
      <Box
        component="form"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onSubmit(event);
        }}
      >
        <DialogTitle id={titleId}>{t(titleKey)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {children}
          </Stack>
          {submitError ? <ErrorState error={submitError} compact /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={requestClose} disabled={isSubmitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting} data-testid="form-dialog-submit">
            {t(submitKey ?? 'common:actions.save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
